import { memo, useState, useEffect } from 'react';
import { Sparkles, Loader2 } from 'lucide-react';
import { VIBES } from './constants';

const SHOWCASE_STYLES = [
  { name: 'Quiet Luxury', bg: '#F5F0EB', accent: '#8B8682', text: '#2A2725' },
  { name: 'Cinematic Dark', bg: '#0F0E0D', accent: '#C9A84C', text: '#E8E6E3' },
  { name: 'Light & Airy', bg: '#FDF8F3', accent: '#C4A095', text: '#2D2420' },
  { name: 'Editorial', bg: '#FAFAFA', accent: '#B89B55', text: '#1A1A1A' },
  { name: 'Film & Analog', bg: '#F0ECE4', accent: '#9B8B6B', text: '#2A2622' },
];

const RightPanel = memo(({ step, style, loading, progress, loadingMsg }) => {
  const [showcaseIdx, setShowcaseIdx] = useState(0);

  // Cycle through showcase styles for early steps
  useEffect(() => {
    if (step >= 3) return;
    const timer = setInterval(() => setShowcaseIdx(i => (i + 1) % SHOWCASE_STYLES.length), 3000);
    return () => clearInterval(timer);
  }, [step]);

  const showcase = SHOWCASE_STYLES[showcaseIdx];

  return (
    <div
      className={`${loading ? 'hidden lg:flex' : 'hidden lg:flex'} flex-1 items-center justify-center p-6 lg:p-12 relative overflow-hidden`}
      style={{ background: 'rgba(242,239,233,0.03)' }}
    >
    <div className="orb orb-1" style={{ top: '20%', left: '10%' }} />
    <div className="orb orb-2" style={{ bottom: '20%', right: '10%' }} />

    {step < 3 && (
      <div className="text-center relative z-10" style={{ width: '100%', maxWidth: '480px' }}>
        {/* Animated wireframe preview — cycles through styles */}
        <div style={{
          borderRadius: '12px', overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.12)',
          transition: 'all 0.8s ease',
        }}>
          {/* Browser chrome */}
          <div style={{ height: '28px', background: '#1a1917', display: 'flex', alignItems: 'center', padding: '0 10px', gap: '5px' }}>
            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#ff5f57' }} />
            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#febd2e' }} />
            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#28c840' }} />
            <span style={{ marginLeft: '8px', fontSize: '8px', color: 'rgba(255,255,255,0.2)', fontFamily: 'var(--mono)' }}>yourstudio.goldenhour.build</span>
          </div>
          {/* Wireframe body — represents the style */}
          <div style={{
            background: showcase.bg, padding: '24px', minHeight: '280px',
            transition: 'background 0.8s ease',
          }}>
            {/* Nav wireframe */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px', opacity: 0.6 }}>
              <div style={{ width: '80px', height: '8px', borderRadius: '4px', background: showcase.accent, transition: 'background 0.8s' }} />
              <div style={{ display: 'flex', gap: '12px' }}>
                {[40, 50, 35].map((w, i) => <div key={i} style={{ width: `${w}px`, height: '5px', borderRadius: '3px', background: showcase.text, opacity: 0.15, transition: 'background 0.8s' }} />)}
              </div>
            </div>
            {/* Hero wireframe */}
            <div style={{ textAlign: 'center', marginBottom: '28px' }}>
              <div style={{ width: '48px', height: '5px', borderRadius: '3px', background: showcase.accent, margin: '0 auto 12px', opacity: 0.4, transition: 'background 0.8s' }} />
              <div style={{ width: '200px', height: '18px', borderRadius: '4px', background: showcase.text, margin: '0 auto 8px', opacity: 0.12, transition: 'background 0.8s' }} />
              <div style={{ width: '140px', height: '10px', borderRadius: '4px', background: showcase.text, margin: '0 auto 20px', opacity: 0.06, transition: 'background 0.8s' }} />
              <div style={{ width: '100px', height: '28px', borderRadius: '6px', background: showcase.accent, margin: '0 auto', opacity: 0.2, transition: 'background 0.8s' }} />
            </div>
            {/* Gallery wireframe */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '6px', marginBottom: '20px' }}>
              {[70, 90, 70].map((h, i) => <div key={i} style={{ height: `${h}px`, borderRadius: '4px', background: showcase.accent, opacity: 0.08, transition: 'background 0.8s' }} />)}
            </div>
            {/* About wireframe */}
            <div style={{ display: 'flex', gap: '16px', opacity: 0.5 }}>
              <div style={{ width: '80px', height: '80px', borderRadius: '4px', background: showcase.accent, opacity: 0.12, flexShrink: 0, transition: 'background 0.8s' }} />
              <div style={{ flex: 1 }}>
                <div style={{ width: '80px', height: '8px', borderRadius: '4px', background: showcase.text, marginBottom: '8px', opacity: 0.1, transition: 'background 0.8s' }} />
                <div style={{ width: '100%', height: '5px', borderRadius: '3px', background: showcase.text, marginBottom: '4px', opacity: 0.05 }} />
                <div style={{ width: '85%', height: '5px', borderRadius: '3px', background: showcase.text, opacity: 0.05 }} />
              </div>
            </div>
          </div>
        </div>
        <p style={{ fontFamily: 'var(--sans)', fontSize: '12px', color: '#8c8782', marginTop: '14px', transition: 'opacity 0.5s' }}>
          {showcase.name} style
        </p>
        {/* Dots */}
        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', marginTop: '8px' }}>
          {SHOWCASE_STYLES.map((_, i) => (
            <div key={i} style={{ width: i === showcaseIdx ? '16px' : '4px', height: '4px', borderRadius: '2px', background: i === showcaseIdx ? '#C9A84C' : '#e8e3dc', transition: 'all 0.3s' }} />
          ))}
        </div>
      </div>
    )}

    {step >= 3 && style && !loading && step < 5 && (
      <div className="text-center relative z-10">
        <div className="browser-mockup w-[400px] overflow-hidden" style={{ boxShadow: '0 30px 80px rgba(42,37,32,0.15)' }}>
          <div className="browser-mockup-bar">
            <div className="browser-dot" style={{ background: '#FF605C' }} />
            <div className="browser-dot" style={{ background: '#FFBD44' }} />
            <div className="browser-dot" style={{ background: '#00CA4E' }} />
          </div>
          <div className="p-6" style={{ background: VIBES.find(v => v.key === style)?.colors[0] || '#faf8f5', minHeight: '280px' }}>
            <div className="mb-4">
              <div className="w-20 h-1.5 rounded mb-2" style={{ background: VIBES.find(v => v.key === style)?.colors[1] || '#C9A84C' }} />
              <div className="w-32 h-3 rounded mb-1" style={{ background: VIBES.find(v => v.key === style)?.colors[2] || '#2a2725', opacity: 0.7 }} />
              <div className="w-24 h-2 rounded" style={{ background: VIBES.find(v => v.key === style)?.colors[2] || '#2a2725', opacity: 0.3 }} />
            </div>
            <div className="w-full h-32 rounded-lg mb-3" style={{ background: VIBES.find(v => v.key === style)?.colors[1] || '#C9A84C', opacity: 0.2 }} />
            <div className="grid grid-cols-3 gap-2">
              <div className="h-16 rounded" style={{ background: VIBES.find(v => v.key === style)?.colors[1] || '#C9A84C', opacity: 0.15 }} />
              <div className="h-16 rounded" style={{ background: VIBES.find(v => v.key === style)?.colors[1] || '#C9A84C', opacity: 0.1 }} />
              <div className="h-16 rounded" style={{ background: VIBES.find(v => v.key === style)?.colors[1] || '#C9A84C', opacity: 0.15 }} />
            </div>
          </div>
        </div>
        <p className="mt-4 font-sans text-xs" style={{ color: '#8c8782' }}>
          {VIBES.find(v => v.key === style)?.label} preview
        </p>
      </div>
    )}

    {loading && (
      <div className="relative z-10 w-full h-full flex flex-col items-center justify-center p-8">
        {/* Progress */}
        <div className="w-full max-w-lg mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="font-serif text-lg font-bold" style={{ color: 'inherit' }}>Crafting your vision...</span>
            <span className="font-sans text-lg font-bold" style={{ color: '#C9A84C' }}>{Math.round(progress)}%</span>
          </div>
          <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: '#e8e3dc' }}>
            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${progress}%`, background: 'linear-gradient(90deg, #C9A84C, #c46a4e)' }} />
          </div>
          <p className="font-sans text-xs italic mt-2 transition-opacity duration-300" style={{ color: '#8c8782' }} key={loadingMsg}>{loadingMsg}</p>
        </div>

        {/* Animated site skeleton that looks like a site being built */}
        <div className="w-full max-w-lg" style={{ background: VIBES.find(v => v.key === style)?.colors[0] || '#0e0d0b', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 30px 80px rgba(0,0,0,0.3)' }}>
          {/* Fake nav */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', opacity: progress > 10 ? 1 : 0, transition: 'opacity 1s' }}>
            <div style={{ width: '80px', height: '8px', borderRadius: '4px', background: 'rgba(196,162,101,0.2)' }} />
            <div style={{ display: 'flex', gap: '16px' }}>
              {[40, 50, 35, 45].map((w, i) => <div key={i} style={{ width: `${w}px`, height: '6px', borderRadius: '3px', background: '#e8e3dc' }} />)}
            </div>
          </div>
          {/* Fake hero */}
          <div style={{ padding: '40px 24px 48px', opacity: progress > 20 ? 1 : 0, transition: 'opacity 1.5s' }}>
            <div style={{ width: '60px', height: '6px', borderRadius: '3px', background: 'rgba(196,162,101,0.15)', marginBottom: '16px' }} />
            <div style={{ width: '280px', height: '24px', borderRadius: '4px', background: '#f0ece6', marginBottom: '8px' }} />
            <div style={{ width: '200px', height: '12px', borderRadius: '4px', background: '#f5f2ee', marginBottom: '24px' }} />
            <div style={{ width: '120px', height: '32px', borderRadius: '6px', background: 'rgba(196,162,101,0.12)' }} />
          </div>
          {/* Fake gallery grid */}
          <div style={{ padding: '0 24px 24px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', opacity: progress > 45 ? 1 : 0, transition: 'opacity 1.5s' }}>
            {[80, 100, 80, 100, 80, 100].map((h, i) => <div key={i} style={{ height: `${h}px`, borderRadius: '4px', background: 'rgba(196,162,101,0.06)' }} />)}
          </div>
          {/* Fake about section */}
          <div style={{ padding: '24px', display: 'flex', gap: '24px', opacity: progress > 60 ? 1 : 0, transition: 'opacity 1.5s' }}>
            <div style={{ width: '120px', height: '120px', borderRadius: '4px', background: 'rgba(196,162,101,0.08)', flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ width: '100px', height: '10px', borderRadius: '4px', background: '#f0ece6', marginBottom: '12px' }} />
              <div style={{ width: '100%', height: '6px', borderRadius: '3px', background: 'rgba(242,239,233,0.03)', marginBottom: '6px' }} />
              <div style={{ width: '90%', height: '6px', borderRadius: '3px', background: 'rgba(242,239,233,0.03)', marginBottom: '6px' }} />
              <div style={{ width: '75%', height: '6px', borderRadius: '3px', background: 'rgba(242,239,233,0.03)' }} />
            </div>
          </div>
        </div>
      </div>
    )}
    </div>
  );
});

RightPanel.displayName = 'RightPanel';
export default RightPanel;
